import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  RecaptchaVerifier,
  signInWithPhoneNumber,
  onAuthStateChanged,
  updateProfile
} from "firebase/auth";
import {  
  getFirestore,
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
  orderBy,
  limit,
  writeBatch,
  deleteDoc
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from "firebase/messaging";

// Firebase Configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || `${process.env.REACT_APP_FIREBASE_PROJECT_ID}.appspot.com`,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "default-sender-id",
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ""
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Initialize Messaging (with compatibility check)
let messaging;
const initializeMessaging = async () => {
  try {
    if (await isSupported()) {
      messaging = getMessaging(app);
    }
  } catch (error) {
    console.error('Firebase Messaging not supported', error);
  }
};
initializeMessaging();

// Auth State Listener with User Profile Management
export const setupAuthStateListener = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      const userData = {
        displayName: user.displayName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        photoURL: user.photoURL,
        lastLogin: serverTimestamp(),
        updatedAt: serverTimestamp(),
        fcmToken: null,
        notificationEnabled: false,
        subscriptions: []
      };

      if (!userSnap.exists()) {
        await setDoc(userRef, {
          ...userData,
          createdAt: serverTimestamp(),
          role: 'student' // Default role is student
        });
      } else {
        await updateDoc(userRef, userData);
      }
    }
    callback(user);
  });
};

// Teacher Service - خدمة جديدة للمعلمين
export const teacherService = {
  // الحصول على المواد التي يدرسها المعلم
  async getTeacherCourses(teacherId) {
    try {
      const q = query(
        collection(db, 'courses'),
        where('instructorId', '==', teacherId)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting teacher courses:', error);
      throw error;
    }
  },

  // الحصول على إحصائيات المواد الخاصة بالمعلم
  async getTeacherStatistics(teacherId) {
    try {
      const courses = await this.getTeacherCourses(teacherId);
      const statistics = [];
      
      for (const course of courses) {
        // الحصول على عدد الطلاب المسجلين في المادة
        const subscriptionsQuery = query(
          collection(db, 'subscriptions'),
          where('courseId', '==', course.id),
          where('status', '==', 'active')
        );
        const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
        const studentCount = subscriptionsSnapshot.size;
        
        // الحصول على التقييمات للمادة
        const ratingsQuery = query(
          collection(db, 'ratings'),
          where('courseId', '==', course.id)
        );
        const ratingsSnapshot = await getDocs(ratingsQuery);
        
        let totalRating = 0;
        let averageRating = 0;
        
        if (!ratingsSnapshot.empty) {
          ratingsSnapshot.forEach(doc => {
            totalRating += doc.data().rating;
          });
          averageRating = totalRating / ratingsSnapshot.size;
        }
        
        statistics.push({
          courseId: course.id,
          courseTitle: course.title,
          studentCount,
          averageRating: Math.round(averageRating * 10) / 10,
          totalRatings: ratingsSnapshot.size,
          totalRevenue: studentCount * (course.price || 0)
        });
      }
      
      return statistics;
    } catch (error) {
      console.error('Error getting teacher statistics:', error);
      throw error;
    }
  },

  // الحصول على طلبات الاشتراك للمواد الخاصة بالمعلم
  async getTeacherSubscriptionRequests(teacherId) {
    try {
      const courses = await this.getTeacherCourses(teacherId);
      const courseIds = courses.map(course => course.id);
      
      if (courseIds.length === 0) return [];
      
      const requestsQuery = query(
        collection(db, 'subscriptionRequests'),
        where('courseId', 'in', courseIds),
        where('status', '==', 'pending')
      );
      
      const querySnapshot = await getDocs(requestsQuery);
      const requests = [];
      
      for (const doc of querySnapshot.docs) {
        const requestData = doc.data();
        
        // جلب معلومات إضافية عن الطالب
        let studentName = requestData.studentName;
        if (!studentName) {
          try {
            const studentDoc = await getDoc(doc(db, 'users', requestData.studentId));
            if (studentDoc.exists()) {
              studentName = studentDoc.data().name || studentDoc.data().displayName || "Unknown";
            }
          } catch (error) {
            console.error('Error fetching student:', error);
          }
        }
        
        requests.push({
          id: doc.id,
          ...requestData,
          studentName,
          timestamp: requestData.timestamp?.toDate() || new Date()
        });
      }
      
      return requests;
    } catch (error) {
      console.error('Error getting teacher subscription requests:', error);
      throw error;
    }
  },

  // الحصول على إحصائيات عامة للمعلم
  async getTeacherOverview(teacherId) {
    try {
      const statistics = await this.getTeacherStatistics(teacherId);
      
      const totalStudents = statistics.reduce((sum, stat) => sum + stat.studentCount, 0);
      const totalRevenue = statistics.reduce((sum, stat) => sum + stat.totalRevenue, 0);
      const averageRating = statistics.length > 0 
        ? statistics.reduce((sum, stat) => sum + stat.averageRating, 0) / statistics.length 
        : 0;
      
      return {
        totalCourses: statistics.length,
        totalStudents,
        totalRevenue,
        averageRating: Math.round(averageRating * 10) / 10,
        pendingRequests: (await this.getTeacherSubscriptionRequests(teacherId)).length
      };
    } catch (error) {
      console.error('Error getting teacher overview:', error);
      throw error;
    }
  }
};

// Subscription Requests Service - خدمة طلبات الاشتراك
export const subscriptionRequestsService = {
  // إنشاء طلب اشتراك جديد
  async createSubscriptionRequest(userId, courseId, paymentData) {
    try {
      const requestData = {
        studentId: userId,
        courseId: courseId,
        proofUrl: paymentData.proofUrl || '',
        paymentMethod: paymentData.method || 'wallet',
        paymentAmount: paymentData.amount || 0,
        status: 'pending',
        timestamp: serverTimestamp(),
        studentName: paymentData.studentName || '',
        courseName: paymentData.courseName || '',
        reviewedBy: null,
        reviewedAt: null
      };

      const requestRef = await addDoc(collection(db, 'subscriptionRequests'), requestData);
      
      // إرسال إشعار للمسؤولين
      await this.notifyAdminsAboutNewRequest(requestRef.id, requestData);
      
      return requestRef.id;
    } catch (error) {
      console.error('Error creating subscription request:', error);
      throw error;
    }
  },

  // الحصول على جميع طلبات الاشتراك
  async getAllSubscriptionRequests(status = null) {
    try {
      let q;
      if (status) {
        q = query(collection(db, 'subscriptionRequests'), where('status', '==', status));
      } else {
        q = query(collection(db, 'subscriptionRequests'));
      }
      
      const querySnapshot = await getDocs(q);
      const requests = [];
      
      for (const doc of querySnapshot.docs) {
        const requestData = doc.data();
        
        // جلب معلومات إضافية عن الطالب
        let studentName = requestData.studentName;
        if (!studentName) {
          try {
            const studentQuery = query(collection(db, 'users'), where('uid', '==', requestData.studentId));
            const studentSnapshot = await getDocs(studentQuery);
            if (!studentSnapshot.empty) {
              studentName = studentSnapshot.docs[0].data().name || studentSnapshot.docs[0].data().displayName || "Unknown";
            }
          } catch (error) {
            console.error('Error fetching student:', error);
          }
        }
        
        // جلب معلومات إضافية عن الكورس
        let courseName = requestData.courseName;
        let coursePrice = requestData.paymentAmount;
        if (!courseName) {
          try {
            const courseQuery = query(collection(db, 'courses'), where('id', '==', requestData.courseId));
            const courseSnapshot = await getDocs(courseQuery);
            if (!courseSnapshot.empty) {
              const courseData = courseSnapshot.docs[0].data();
              courseName = courseData.title || "Unknown";
              coursePrice = courseData.price || requestData.paymentAmount;
            }
          } catch (error) {
            console.error('Error fetching course:', error);
          }
        }
        
        requests.push({
          id: doc.id,
          ...requestData,
          studentName,
          courseName,
          coursePrice,
          timestamp: requestData.timestamp?.toDate() || new Date(),
          reviewedAt: requestData.reviewedAt?.toDate() || null
        });
      }
      
      // ترتيب الطلبات بحيث تكون الطلبات المعلقة أولاً
      return requests.sort((a, b) => {
        if (a.status === 'pending' && b.status !== 'pending') return -1;
        if (a.status !== 'pending' && b.status === 'pending') return 1;
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
    } catch (error) {
      console.error('Error getting subscription requests:', error);
      throw error;
    }
  },

  // الموافقة على طلب اشتراك
  async approveSubscriptionRequest(requestId, adminId, adminName) {
    try {
      const requestRef = doc(db, 'subscriptionRequests', requestId);
      const requestSnap = await getDoc(requestRef);
      
      if (!requestSnap.exists()) {
        throw new Error('Subscription request not found');
      }
      
      const requestData = requestSnap.data();
      
      // تحديث حالة الطلب
      await updateDoc(requestRef, {
        status: 'approved',
        reviewedBy: adminId,
        reviewedAt: serverTimestamp(),
        adminName: adminName
      });
      
      // تفعيل الاشتراك للطالب
      await subscriptionService.add(
        requestData.studentId, 
        requestData.courseId, 
        {
          method: requestData.paymentMethod,
          amount: requestData.paymentAmount,
          status: 'confirmed',
          proofUrl: requestData.proofUrl,
          adminId: adminId,
          adminName: adminName
        }
      );
      
      // إرسال إشعار للطالب
      await notificationService.createNotification(requestData.studentId, {
        title: 'تمت الموافقة على طلب الاشتراك',
        body: `تمت الموافقة على طلب اشتراكك في الكورس "${requestData.courseName}"`,
        type: 'subscription_approved',
        data: {
          courseId: requestData.courseId,
          requestId: requestId
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error approving subscription request:', error);
      throw error;
    }
  },

  // رفض طلب اشتراك
  async rejectSubscriptionRequest(requestId, adminId, adminName, reason = '') {
    try {
      const requestRef = doc(db, 'subscriptionRequests', requestId);
      const requestSnap = await getDoc(requestRef);
      
      if (!requestSnap.exists()) {
        throw new Error('Subscription request not found');
      }
      
      const requestData = requestSnap.data();
      
      // تحديث حالة الطلب
      await updateDoc(requestRef, {
        status: 'rejected',
        reviewedBy: adminId,
        reviewedAt: serverTimestamp(),
        adminName: adminName,
        rejectionReason: reason
      });
      
      // إرسال إشعار للطالب
      await notificationService.createNotification(requestData.studentId, {
        title: 'تم رفض طلب الاشتراك',
        body: `تم رفض طلب اشتراكك في الكورس "${requestData.courseName}"${reason ? `. السبب: ${reason}` : ''}`,
        type: 'subscription_rejected',
        data: {
          courseId: requestData.courseId,
          requestId: requestId,
          reason: reason
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error rejecting subscription request:', error);
      throw error;
    }
  },

  // إرسال إشعار للمسؤولين بطلب جديد
  async notifyAdminsAboutNewRequest(requestId, requestData) {
    try {
      // الحصول على جميع المسؤولين
      const adminsQuery = query(collection(db, 'users'), where('role', '==', 'admin'));
      const adminsSnapshot = await getDocs(adminsQuery);
      
      for (const adminDoc of adminsSnapshot.docs) {
        const adminData = adminDoc.data();
        await notificationService.createNotification(adminDoc.id, {
          title: 'طلب اشتراك جديد',
          body: `طلب اشتراك جديد من ${requestData.studentName} في كورس ${requestData.courseName}`,
          type: 'new_subscription_request',
          data: {
            requestId: requestId,
            studentId: requestData.studentId,
            courseId: requestData.courseId
          }
        });
      }
    } catch (error) {
      console.error('Error notifying admins:', error);
    }
  }
};

// Notification Service
export const notificationService = {
  messagingInstance: messaging,

  async requestPermission(userId) {
    if (!this.messagingInstance) {
      console.warn('Messaging not supported');
      return null;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const token = await getToken(this.messagingInstance, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
        });
        
        if (userId) {
          await updateDoc(doc(db, 'users', userId), {
            fcmToken: token,
            notificationEnabled: true,
            updatedAt: serverTimestamp()
          });
        }
        
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error getting notification permission:', error);
      return null;
    }
  },

  async createNotification(userId, notificationData) {
    try {
      const notification = {
        userId,
        title: notificationData.title,
        body: notificationData.body || notificationData.message,
        read: false,
        createdAt: serverTimestamp(),
        type: notificationData.type || 'general',
        data: notificationData.data || {}
      };

      const notificationRef = await addDoc(collection(db, 'notifications'), notification);
      return notificationRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  },

  async getUserNotifications(userId, limitCount = 20) {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null
      }));
    } catch (error) {
      console.error('Error getting notifications:', error);
      return [];
    }
  },

  async markAsRead(notificationId, userId) {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, { 
        read: true,
        readAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  },

  async markAllAsRead(userId) {
    try {
      const notifications = await this.getUserNotifications(userId);
      const batch = writeBatch(db);
      
      notifications
        .filter(n => !n.read)
        .forEach(notification => {
          const notificationRef = doc(db, 'notifications', notification.id);
          batch.update(notificationRef, { 
            read: true,
            readAt: serverTimestamp()
          });
        });
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  },

  async deleteNotification(notificationId) {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  },

  onMessageListener() {
    if (!this.messagingInstance) {
      return new Promise(() => {});
    }

    return new Promise((resolve) => {
      onMessage(this.messagingInstance, (payload) => {
        resolve(payload);
      });
    });
  },

  async deleteToken(userId) {
    if (!this.messagingInstance) return;
    
    try {
      await deleteToken(this.messagingInstance);
      if (userId) {
        await updateDoc(doc(db, 'users', userId), {
          fcmToken: null,
          notificationEnabled: false,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error deleting token:', error);
    }
  }
};

// Subscription Service
export const subscriptionService = {
  async add(userId, courseId, paymentData = {}) {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + (paymentData.durationDays || 30));
    
    const subscriptionDoc = {
      userId,
      courseId,
      status: 'active',
      expiresAt,
      createdAt: serverTimestamp(),
      paymentMethod: paymentData.method || 'cash',
      paymentStatus: paymentData.status || 'confirmed',
      paymentAmount: paymentData.amount || 0,
      paymentProof: paymentData.proofUrl || '',
      adminId: paymentData.adminId || null,
      adminName: paymentData.adminName || null
    };

    await setDoc(doc(db, 'subscriptions', `${userId}_${courseId}`), subscriptionDoc);
    
    // Update user's subscriptions array
    await updateDoc(doc(db, 'users', userId), {
      subscriptions: arrayUnion(courseId),
      updatedAt: serverTimestamp()
    });

    // Send notification
    const courseDoc = await getDoc(doc(db, 'courses', courseId));
    const courseData = courseDoc.data();
    
    await notificationService.createNotification(userId, {
      title: 'New Subscription',
      body: `Your subscription to "${courseData?.title || 'new course'}" has been activated`,
      type: 'subscription',
      data: {
        courseId,
        click_action: 'FLUTTER_NOTIFICATION_CLICK'
      }
    });
    
    return true;
  },

  async check(userId, courseId) {
    try {
      const subscriptionSnap = await getDoc(doc(db, 'subscriptions', `${userId}_${courseId}`));
      
      if (!subscriptionSnap.exists()) return false;
      
      const subscriptionData = subscriptionSnap.data();
      const isActive = subscriptionData.status === 'active' &&
                      subscriptionData.expiresAt.toDate() > new Date();

      // Auto-renew if payment is active but subscription expired
      if (!isActive && subscriptionData.paymentStatus === 'confirmed') {
        return this.renewSubscription(userId, courseId);
      }

      return isActive;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  },

  async renewSubscription(userId, courseId, durationInDays = 30) {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + durationInDays);
    
    await updateDoc(doc(db, 'subscriptions', `${userId}_${courseId}`), {
      status: 'active',
      expiresAt,
      updatedAt: serverTimestamp()
    });
    
    return true;
  }
};

// Payment Service
export const paymentService = {
  async createCashPayment(userId, courseId, receiptImage) {
    try {
      // Upload payment proof
      const proofUrl = await storageService.uploadFile(
        userId, 
        receiptImage, 
        'payment-proofs'
      );
      
      // Create pending subscription
      await setDoc(doc(db, 'subscriptions', `${userId}_${courseId}`), {
        userId,
        courseId,
        paymentMethod: 'cash',
        paymentStatus: 'pending',
        paymentProof: proofUrl,
        createdAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Cash payment error:', error);
      throw error;
    }
  },
  
  async confirmCashPayment(subscriptionId) {
    try {
      const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
      const subscriptionSnap = await getDoc(subscriptionRef);
      
      if (!subscriptionSnap.exists()) {
        throw new Error('Subscription not found');
      }
      
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30); // Default 30 days
      
      await updateDoc(subscriptionRef, {
        paymentStatus: 'confirmed',
        status: 'active',
        expiresAt,
        confirmedAt: serverTimestamp()
      });
      
      // Update user's subscriptions
      const { userId, courseId } = subscriptionSnap.data();
      await updateDoc(doc(db, 'users', userId), {
        subscriptions: arrayUnion(courseId),
        updatedAt: serverTimestamp()
      });

      // Send notification
      await notificationService.createNotification(userId, {
        title: 'Payment Confirmed',
        body: 'Your payment has been confirmed and subscription activated',
        type: 'payment'
      });
      
      return true;
    } catch (error) {
      console.error('Error confirming payment:', error);
      throw error;
    }
  },

  // دفع عبر المحفظة
  async createWalletPayment(userId, courseId, paymentData) {
    try {
      // إنشاء طلب اشتراك جديد
      const requestId = await subscriptionRequestsService.createSubscriptionRequest(
        userId,
        courseId,
        {
          proofUrl: paymentData.proofUrl,
          method: 'wallet',
          amount: paymentData.amount,
          studentName: paymentData.studentName,
          courseName: paymentData.courseName
        }
      );
      
      return requestId;
    } catch (error) {
      console.error('Wallet payment error:', error);
      throw error;
    }
  }
};

// Storage Service
export const storageService = {
  async uploadFile(userId, file, path = 'uploads') {
    try {
      const storageRef = ref(storage, `${path}/${userId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  async getFileUrl(path) {
    try {
      const fileRef = ref(storage, path);
      return await getDownloadURL(fileRef);
    } catch (error) {
      console.error('Error getting file URL:', error);
      throw error;
    }
  },

  async deleteFile(path) {
    try {
      const fileRef = ref(storage, path);
      await deleteObject(fileRef);
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }
};

// Export core Firebase functions
export { 
  doc, 
  getDoc, 
  setDoc,
  collection, 
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
  orderBy,
  limit
};

// Export app for testing
export { app };